/**
 * Size Unit Switcher
 * Allows users to toggle variant size labels between EU, US Men, US Women, UK, and CM.
 * Reads conversion data from existing size-guide HTML tables in the DOM.
 * Also updates sticky cart select options to match the selected unit.
 */
if (typeof window.SizeUnitSwitcher === 'undefined') {
  class SizeUnitSwitcher {
    constructor(productTags, idSuffix) {
      this.productTags = productTags || [];
      this.idSuffix = idSuffix || '';
      this.sizeMap = {};
      this.translations = window.theme?.translations?.product_options || {};
      this.originalOptionTexts = new Map();

      this.brand = this.detectBrand();
      if (!this.brand) return;

      this.buildSizeMap();
      if (Object.keys(this.sizeMap).length === 0) return;

      this.storeOriginalOptionTexts();
      this.currentUnit = this.loadPreference() || 'eu';
      this.bindEvents();
      this.applyButtonLabels();
      this.applyUnit(this.currentUnit);
      this.setActiveButton(this.currentUnit);
    }

    detectBrand() {
      var tagToBrand = {
        'nike-size': 'nike',
        'adidas-size': 'adidas',
        'yeezy-slide-size': 'yeezy',
        'yeezy-foam-size': 'yeezy',
        'mind-001-size': 'mind001',
        'nb-size': 'nb',
        'ugg-size': 'ugg',
        'crocs-size': 'crocs',
        'puma-size': 'puma',
        'asics-size': 'asics',
        'salomon-size': 'salomon',
        'converse-size': 'converse',
        'onitsuka-size': 'onitsuka',
        'onrunning-size': 'onrunning',
        'maison-mihara-yasuhiro-size': 'maisonmiharayasuhiro',
        'timberland-size': 'timberland',
        'birkenstock-size': 'birkenstock',
        'vans-size': 'vans',
        'saucony-size': 'saucony',
        'under-armour-size': 'underarmour'
      };
      for (var i = 0; i < this.productTags.length; i++) {
        var tagLower = this.productTags[i].toLowerCase();
        if (tagToBrand[tagLower]) return tagToBrand[tagLower];
      }
      return null;
    }

    buildSizeMap() {
      var table = document.querySelector(
        '[data-brand="' + this.brand + '"] .size-chart tbody'
      );
      if (!table) return;

      var rows = table.querySelectorAll('tr');
      var self = this;
      rows.forEach(function(row) {
        var cells = row.querySelectorAll('td');
        if (cells.length >= 5) {
          var euRaw = cells[0].textContent.trim();
          var key = self.normalizeSize(euRaw);
          self.sizeMap[key] = {
            eu: euRaw,
            cm: cells[1].textContent.trim(),
            usMan: cells[2].textContent.trim(),
            usWoman: cells[3].textContent.trim(),
            uk: cells[4].textContent.trim()
          };
        }
      });
    }

    normalizeSize(str) {
      return str.trim().replace(/\s+/g, ' ');
    }

    storeOriginalOptionTexts() {
      var selects = ['StickyVariantSelect', 'StickyVariantSelectMobile'];
      var self = this;
      selects.forEach(function(selectId) {
        var select = document.getElementById(selectId);
        if (!select) return;
        var options = select.querySelectorAll('option');
        options.forEach(function(opt) {
          self.originalOptionTexts.set(selectId + '_' + opt.value, opt.textContent.trim());
        });
      });
    }

    replaceSizeInText(text, unit) {
      var self = this;
      var keys = Object.keys(this.sizeMap);
      // Sort by length descending to match longer sizes first (e.g., "42.5" before "42")
      keys.sort(function(a, b) { return b.length - a.length; });

      for (var i = 0; i < keys.length; i++) {
        var euSize = keys[i];
        var mapping = self.sizeMap[euSize];
        var target = mapping[unit];
        if (!target) continue;

        // Match the EU size as a standalone token (not part of a larger number)
        // Use word-boundary-like matching: preceded by start/space/slash, followed by end/space/slash
        var escaped = euSize.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        var regex = new RegExp('(^|[\\s\\/])' + escaped + '($|[\\s\\/])', 'g');
        text = text.replace(regex, function(match, before, after) {
          return before + target + after;
        });

        // Also match if the EU size is the entire string (after trim)
        if (self.normalizeSize(text) === euSize) {
          text = target;
        }
      }
      return text;
    }

    applyButtonLabels() {
      var container = document.getElementById('size-unit-switcher' + this.idSuffix);
      if (!container) return;

      var btnMap = {
        eu: this.translations.size_unit_eu || 'EU',
        usMan: this.translations.size_unit_us_men || 'US M',
        usWoman: this.translations.size_unit_us_women || 'US W',
        uk: this.translations.size_unit_uk || 'UK',
        cm: this.translations.size_unit_cm || 'CM'
      };

      container.querySelectorAll('.size-unit-btn').forEach(function(btn) {
        var unit = btn.getAttribute('data-unit');
        if (btnMap[unit]) {
          btn.textContent = btnMap[unit];
        }
      });
    }

    bindEvents() {
      var container = document.getElementById('size-unit-switcher' + this.idSuffix);
      if (!container) return;

      var self = this;
      this._clickHandler = function(e) {
        var btn = e.target.closest('.size-unit-btn');
        if (!btn) return;

        var unit = btn.getAttribute('data-unit');
        self.applyUnit(unit);
        self.savePreference(unit);
        self.setActiveButton(unit);
      };
      container.addEventListener('click', this._clickHandler);
    }

    setActiveButton(unit) {
      var container = document.getElementById('size-unit-switcher' + this.idSuffix);
      if (!container) return;

      container.querySelectorAll('.size-unit-btn').forEach(function(btn) {
        btn.classList.remove('size-unit-btn--active');
        if (btn.getAttribute('data-unit') === unit) {
          btn.classList.add('size-unit-btn--active');
        }
      });
    }

    applyUnit(unit) {
      this.currentUnit = unit;

      // 1. Update variant radio button labels
      var scope = document;
      if (this.idSuffix === '-quick-view') {
        scope = document.querySelector('#Product-Drawer-Content') || document;
      }

      var sizeLabels = scope.querySelectorAll('.variant-size-text');
      var self = this;

      sizeLabels.forEach(function(label) {
        var labelEl = label.closest('label');
        if (!labelEl) return;
        var radioInput = labelEl.previousElementSibling;
        if (!radioInput || radioInput.type !== 'radio') return;

        var euValue = radioInput.value;
        var normalizedEu = self.normalizeSize(euValue);
        var mapping = self.sizeMap[normalizedEu];

        if (mapping && mapping[unit]) {
          label.textContent = (unit === 'eu') ? euValue : mapping[unit];
        }
      });

      // 2. Update sticky cart variant grid buttons
      var stickyBtns = document.querySelectorAll('.sticky-variant-btn-grid');
      stickyBtns.forEach(function(btn) {
        var euValue = btn.getAttribute('data-option-value');
        if (!euValue) return;
        var normalizedEu = self.normalizeSize(euValue);
        var mapping = self.sizeMap[normalizedEu];
        var sizeLabel = btn.querySelector('.sticky-variant-size');
        if (mapping && mapping[unit] && sizeLabel) {
          sizeLabel.textContent = (unit === 'eu') ? euValue : mapping[unit];
        }
      });

      // 3. Update sticky cart select options
      this.updateStickySelectOptions(unit);
    }

    updateStickySelectOptions(unit) {
      var selects = ['StickyVariantSelect', 'StickyVariantSelectMobile'];
      var self = this;

      selects.forEach(function(selectId) {
        var select = document.getElementById(selectId);
        if (!select) return;

        var options = select.querySelectorAll('option');
        options.forEach(function(opt) {
          var originalKey = selectId + '_' + opt.value;
          var originalText = self.originalOptionTexts.get(originalKey);
          if (!originalText) return;

          if (unit === 'eu') {
            opt.textContent = originalText;
          } else {
            opt.textContent = self.replaceSizeInText(originalText, unit);
          }
        });
      });
    }

    loadPreference() {
      try {
        return localStorage.getItem('nextstep_size_unit');
      } catch (e) {
        return null;
      }
    }

    savePreference(unit) {
      try {
        localStorage.setItem('nextstep_size_unit', unit);
      } catch (e) { /* ignore */ }
    }

    destroy() {
      var container = document.getElementById('size-unit-switcher' + this.idSuffix);
      if (container && this._clickHandler) {
        container.removeEventListener('click', this._clickHandler);
      }
    }
  }

  window.SizeUnitSwitcher = SizeUnitSwitcher;
}
